import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Eye, Code, Smartphone, Monitor, Tablet, Copy, Check, Zap, Tag, Users, Building, Palette, Clock, Phone } from 'lucide-react';
import { templateService } from '../../../services/templateService';

const TemplatePreview = ({ template, onClose }) => {
  const [viewMode, setViewMode] = useState('rendered');
  const [deviceView, setDeviceView] = useState('desktop');
  const [variables, setVariables] = useState({});
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  
  useEffect(() => {
    if (template) {
      // Initialize variables with sample data
      const sampleData = generateSampleData(template);
      setVariables(sampleData);
      
      // Load analytics if available
      loadAnalytics();
    }
  }, [template]);

  const loadAnalytics = async () => {
    try {
      const analyticsData = await templateService?.getTemplateAnalytics(template?.id);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const generateSampleData = (template) => {
    const extractedVars = template?.variables || {};
    const sampleData = {};
    
    // Generate sample values based on variable names
    Object.keys(extractedVars)?.forEach(varName => {
      const lowerName = varName?.toLowerCase();
      
      if (lowerName?.includes('firstname') || lowerName?.includes('first_name')) {
        sampleData[varName] = 'John';
      } else if (lowerName?.includes('lastname') || lowerName?.includes('last_name')) {
        sampleData[varName] = 'Doe';
      } else if (lowerName?.includes('company') || lowerName?.includes('organization')) {
        sampleData[varName] = 'Acme Corp';
      } else if (lowerName?.includes('email')) {
        sampleData[varName] = 'john.doe@acmecorp.com';
      } else if (lowerName?.includes('phone')) {
        sampleData[varName] = '+1 (555) 123-4567';
      } else if (lowerName?.includes('industry')) {
        sampleData[varName] = template?.industry || 'Technology';
      } else if (lowerName?.includes('product') || lowerName?.includes('solution')) {
        sampleData[varName] = 'Our Premium Solution';
      } else if (lowerName?.includes('sender') || lowerName?.includes('from')) {
        sampleData[varName] = 'Sarah Johnson';
      } else if (lowerName?.includes('title') || lowerName?.includes('position')) {
        sampleData[varName] = 'Sales Manager';
      } else if (lowerName?.includes('date')) {
        sampleData[varName] = new Date()?.toLocaleDateString();
      } else if (lowerName?.includes('time')) {
        sampleData[varName] = '2:00 PM';
      } else if (lowerName?.includes('percent') || lowerName?.includes('rate')) {
        sampleData[varName] = '25%';
      } else if (lowerName?.includes('amount') || lowerName?.includes('price')) {
        sampleData[varName] = '$1,999';
      } else {
        sampleData[varName] = `[${varName}]`;
      }
    });
    
    return sampleData;
  };

  const renderContent = () => {
    if (viewMode === 'raw') {
      return template?.content;
    }
    
    // Replace variables with values
    let content = template?.content || '';
    Object.entries(variables)?.forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      content = content?.replace(regex, value);
    });
    
    return content;
  };

  const renderSubject = () => {
    if (!template?.subject) return null;
    
    let subject = template?.subject;
    if (viewMode === 'rendered') {
      Object.entries(variables)?.forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        subject = subject?.replace(regex, value);
      });
    }
    
    return subject;
  };

  const handleCopy = async () => {
    try {
      let content = viewMode === 'rendered' ? renderContent() : template?.content;
      const fullContent = template?.subject ? `Subject: ${renderSubject()}\n\n${content}` : content;
      
      await navigator.clipboard?.writeText(fullContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      // Track usage
      await templateService?.trackTemplateUsage(template?.id);
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  const handleVariableChange = (varName, value) => {
    setVariables(prev => ({
      ...prev,
      [varName]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDeviceClass = () => {
    switch (deviceView) {
      case 'mobile':
        return 'max-w-sm mx-auto';
      case 'tablet':
        return 'max-w-2xl mx-auto';
      default:
        return 'max-w-4xl mx-auto';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e?.target === e?.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              {template?.ai_generated ? (
                <Zap className="h-5 w-5 text-blue-600" />
              ) : (
                <Eye className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{template?.name}</h2>
              <p className="text-sm text-gray-600">
                {template?.type?.toUpperCase()} • {template?.category?.replace('_', ' ')}
                {template?.ai_generated && ' • AI Generated'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Device View Toggle */}
            <div className="flex items-center bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setDeviceView('mobile')}
                className={`p-2 rounded transition-colors ${
                  deviceView === 'mobile' ? 'bg-white shadow-sm' : 'text-gray-500'
                }`}
              >
                <Smartphone className="h-4 w-4" />
              </button>
              <button
                onClick={() => setDeviceView('tablet')}
                className={`p-2 rounded transition-colors ${
                  deviceView === 'tablet' ? 'bg-white shadow-sm' : 'text-gray-500'
                }`}
              >
                <Tablet className="h-4 w-4" />
              </button>
              <button
                onClick={() => setDeviceView('desktop')}
                className={`p-2 rounded transition-colors ${
                  deviceView === 'desktop' ? 'bg-white shadow-sm' : 'text-gray-500'
                }`}
              >
                <Monitor className="h-4 w-4" />
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('rendered')}
                className={`px-3 py-2 text-sm rounded transition-colors ${
                  viewMode === 'rendered' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                }`}
              >
                <Eye className="h-4 w-4 inline mr-1" />
                Preview
              </button>
              <button
                onClick={() => setViewMode('raw')}
                className={`px-3 py-2 text-sm rounded transition-colors ${
                  viewMode === 'raw' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                }`}
              >
                <Code className="h-4 w-4 inline mr-1" />
                Raw
              </button>
            </div>

            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex h-[calc(95vh-80px)]">
          {/* Left Sidebar - Template Info & Variables */}
          <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Template Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Template Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium capitalize">{template?.type}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Audience:</span>
                    <span className="font-medium">{template?.target_audience || 'General'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Industry:</span>
                    <span className="font-medium">{template?.industry || 'General'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Palette className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Tone:</span>
                    <span className="font-medium capitalize">{template?.tone || 'Professional'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Updated:</span>
                    <span className="font-medium">{formatDate(template?.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {template?.tags && template?.tags?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {template?.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Performance Analytics */}
              {analytics && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Performance</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Usage Count:</span>
                      <span className="font-medium">{analytics?.usage_count || 0}</span>
                    </div>
                    {analytics?.performance_data?.open_rate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Open Rate:</span>
                        <span className="font-medium">
                          {Math.round(analytics?.performance_data?.open_rate * 100)}%
                        </span>
                      </div>
                    )}
                    {analytics?.performance_data?.response_rate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Response Rate:</span>
                        <span className="font-medium">
                          {Math.round(analytics?.performance_data?.response_rate * 100)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Variables */}
              {Object.keys(variables)?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Variables</h3>
                  <div className="space-y-3">
                    {Object.entries(variables)?.map(([varName, value]) => (
                      <div key={varName}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {varName}
                        </label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleVariableChange(varName, e?.target?.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content Area - Preview */}
          <div className="flex-1 overflow-y-auto bg-white">
            <div className={`p-8 ${getDeviceClass()}`}>
              {/* Email Layout */}
              {template?.type === 'email' && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  {/* Email Header */}
                  <div className="border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {template?.name?.charAt(0) || 'T'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Template Preview</div>
                          <div className="text-xs text-gray-500">preview@template.com</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date()?.toLocaleString()}
                      </div>
                    </div>
                    {renderSubject() && (
                      <div className="text-lg font-semibold text-gray-900">
                        {renderSubject()}
                      </div>
                    )}
                  </div>
                  
                  {/* Email Body */}
                  <div className="p-6">
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap leading-relaxed">
                        {renderContent()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SMS Layout */}
              {template?.type === 'sms' && (
                <div className="max-w-sm mx-auto">
                  <div className="bg-gray-100 rounded-t-xl p-3">
                    <div className="text-center text-xs text-gray-600">SMS Preview</div>
                  </div>
                  <div className="bg-white border-x border-gray-200 p-4">
                    <div className="flex justify-end">
                      <div className="bg-blue-500 text-white p-3 rounded-lg max-w-xs">
                        <div className="text-sm whitespace-pre-wrap">
                          {renderContent()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-100 rounded-b-xl p-3">
                    <div className="text-center text-xs text-gray-600">
                      {new Date()?.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {/* Social Media Layout */}
              {template?.type === 'social_media' && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium">
                          {template?.name?.charAt(0) || 'T'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Your Company</div>
                        <div className="text-sm text-gray-500">
                          {new Date()?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed text-gray-900">
                      {renderContent()}
                    </div>
                  </div>
                </div>
              )}

              {/* Call Script Layout */}
              {template?.type === 'call' && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Phone className="h-5 w-5 text-gray-500" />
                      <h3 className="text-lg font-semibold text-gray-900">Call Script</h3>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap leading-relaxed font-mono text-sm">
                        {renderContent()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TemplatePreview;